/* Websitecontent (ADR 0024): de content van greenvision.energy, in schema `website`.
 * Het CRM-team (admin, super_admin, crm_user) beheert hem; de site leest hem met een eigen rol.
 * De client komt als argument binnen, zodat de toetsen hem kunnen nabootsen. Dit is NIET de oude
 * planningsmodule (public.website_content, website_tasks): die raakt de site niet.
 *
 * Wat hier bewerkbaar is: de vlakke tekstvelden, en van oplossingen, diensten en branches ook de
 * samengestelde blokken (het lijstje, de tekstvakken rechts, de voordelen; zie ./blokken). Een blok
 * wordt alleen geschreven als het is gewijzigd en de bewerker de vorm in de database herkent.
 * Hetzelfde geldt voor de kaart "In het kort" van een oplossing of dienst (./kerngegevens), de plaats
 * van een projectverhaal (./plaats), en het schuifbeeld (./schuifbeeld) en de dag als grafiek (./dag) van
 * een oplossing. Die komen uit kolommen die er pas zijn als de eigenaar van de database een script heeft
 * gedraaid: heeft de rij de kolom niet, dan wordt hij nooit geschreven.
 * De instellingen van de site staan in ./instellingen. Het team, de citaten en de logo's zijn gewone
 * soorten content met een publicatie en staan in ./lijsten; de doorverwijzingen hebben een eigen vorm (./doorverwijzingen),
 * en de vaste pagina's (website.pages) ook (./paginas). projects.solution_type wordt alleen gelezen. */

import { LIJST, TEKSTVAKKEN, VOORDELEN, controleerBlok, leesBlok, zelfdeBlok } from './blokken';
import type { Blok } from './blokken';
import { KOLOM as DAG, controleerDag, heeftDag, teSchrijven as dagTeSchrijven } from './dag';
import { KOLOM as KERNGEGEVENS, controleerKerngegevens, heeftKerngegevens, teSchrijven } from './kerngegevens';
import { LIJSTEN } from './lijsten';
import { controleerPlaats, heeftPlaats, schrijfPlaats } from './plaats';
import { KOLOM as SCHUIFBEELD, controleerSchuifbeeld, heeftSchuifbeeld, teSchrijven as schuifbeeldTeSchrijven } from './schuifbeeld';

export interface WebsiteClient {
  schema(naam: 'website'): { from(tabel: string): any };
}

export type VeldSoort = 'tekst' | 'lang' | 'html' | 'afbeelding' | 'getal';

export interface Veld {
  kolom: string;
  label: string;
  soort: VeldSoort;
  verplicht?: boolean;
  /** Een zin onder het vak: wat de site met deze waarde doet. */
  uitleg?: string;
  /** Wat er mis is met een ingevulde waarde, of null. Alleen een gewijzigde waarde wordt nagekeken:
   *  oude inhoud houdt het opslaan van een ander veld niet tegen. */
  keur?: (waarde: string) => string | null;
}

export interface ContentSoort {
  /** Sleutel in de URL van de CRM en in de query-cache. */
  sleutel: string;
  tabel: string;
  enkelvoud: string;
  meervoud: string;
  /** Pad op de site vóór de slug, of null als het type geen eigen pagina heeft. */
  padPrefix: string | null;
  /** Kolom die als titel in de lijst staat. */
  titelKolom: string;
  heeftSlug: boolean;
  heeftPublicatie: boolean;
  /** De publicatievlag van deze soort komt uit dit script in de site-repo, dat de eigenaar van de
   *  database draait. Heeft een rij de kolom nog niet, dan kent ze geen publicatie (kentPublicatie). */
  publicatieScript?: string;
  volgorde: string;
  oplopend: boolean;
  velden: Veld[];
  /** Samengestelde blokken die hier te bewerken zijn (./blokken). */
  blokken: Blok[];
  /** Kolommen die getoond worden maar hier niet te bewerken zijn. */
  alleenLezen: string[];
  /** De kaart "In het kort" (key_facts) is hier te bewerken (./kerngegevens). */
  kerngegevens?: boolean;
  /** De plaats voor de projectkaart (place, latitude, longitude) is hier te bewerken (./plaats). */
  plaats?: boolean;
  /** Het schuifbeeld (before_after) is hier te bewerken (./schuifbeeld). */
  schuifbeeld?: boolean;
  /** Eén dag als grafiek (day_profile) is hier te bewerken (./dag). */
  dag?: boolean;
  /** Waar dit op de site staat en wat de site ervan toont. Staat boven de lijst en in het formulier. */
  uitleg?: string;
  /** Een waarschuwing die opvalt in het formulier: wat er op de site gebeurt als je opslaat. */
  letOp?: string;
  /** Kolommen die in de lijst onder de titel staan, in deze volgorde. */
  ondertitel?: string[];
  /** Kolom met de afbeelding die in de lijst voor de titel staat. */
  beeld?: string;
  /** De volgorde is met de hand gezet: het volgnummer staat in de lijst, en iets nieuws krijgt het
   *  eerstvolgende nummer en komt dus achteraan. */
  volgnummer?: boolean;
  /** Waarmee het formulier van iets nieuws begint. */
  begin?: Rij;
}

/* De site toont de voordelen alleen op de pagina van een dienst (diensten/[slug] in de site-repo).
 * Bij een energieoplossing staan ze wel in de database, maar niet op de pagina. */
const VOORDELEN_OPLOSSING: Blok = {
  ...VOORDELEN,
  uitleg: 'Kaartjes met een icoon, een titel en een zin. Let op: de site toont ze nu alleen bij diensten, niet op de pagina van een energieoplossing.',
};

const ARTIKEL_VELDEN: Veld[] = [
  { kolom: 'title', label: 'Titel', soort: 'tekst', verplicht: true },
  { kolom: 'date', label: 'Datum (zoals getoond, bv. 27 juni 2026)', soort: 'tekst' },
  { kolom: 'description', label: 'Samenvatting', soort: 'lang' },
  { kolom: 'image', label: 'Afbeelding', soort: 'afbeelding' },
  { kolom: 'content', label: 'Tekst', soort: 'html' },
];

const DETAIL_VELDEN: Veld[] = [
  { kolom: 'title', label: 'Titel', soort: 'tekst', verplicht: true },
  { kolom: 'hero_subtitle', label: 'Ondertitel in de kop', soort: 'tekst' },
  { kolom: 'hero_image', label: 'Afbeelding in de kop', soort: 'afbeelding' },
  { kolom: 'intro_title', label: 'Titel van de inleiding', soort: 'tekst' },
  { kolom: 'intro_text', label: 'Inleiding', soort: 'html' },
  { kolom: 'checklist_title', label: 'Titel van de checklist', soort: 'tekst' },
  { kolom: 'extra_title', label: 'Titel van het extra blok', soort: 'tekst' },
  { kolom: 'extra_text', label: 'Extra blok', soort: 'html' },
  { kolom: 'banner_title', label: 'Titel van de banner', soort: 'tekst' },
  { kolom: 'banner_subtitle', label: 'Ondertitel van de banner', soort: 'tekst' },
  { kolom: 'banner_image', label: 'Afbeelding van de banner', soort: 'afbeelding' },
  { kolom: 'sort_order', label: 'Volgorde', soort: 'getal' },
];

export const SOORTEN: ContentSoort[] = [
  {
    sleutel: 'nieuws', tabel: 'news_articles', enkelvoud: 'Nieuwsbericht', meervoud: 'Nieuws',
    padPrefix: '/nieuws/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'created_at', oplopend: false, velden: ARTIKEL_VELDEN, blokken: [], alleenLezen: [],
  },
  {
    sleutel: 'kennisbank', tabel: 'knowledge_articles', enkelvoud: 'Kennisartikel', meervoud: 'Kennisbank',
    padPrefix: '/kennisartikelen/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'created_at', oplopend: false, velden: ARTIKEL_VELDEN, blokken: [], alleenLezen: [],
  },
  {
    sleutel: 'projectverhalen', tabel: 'projects', enkelvoud: 'Projectverhaal', meervoud: 'Projectverhalen',
    padPrefix: '/projecten/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'created_at', oplopend: false,
    velden: [
      ...ARTIKEL_VELDEN,
      { kolom: 'status', label: 'Status (bv. Gerealiseerd)', soort: 'tekst' },
      { kolom: 'industry', label: 'Branche', soort: 'tekst' },
      { kolom: 'sort_order', label: 'Volgorde', soort: 'getal' },
    ],
    blokken: [], alleenLezen: ['solution_type'], plaats: true,
  },
  {
    sleutel: 'vacatures', tabel: 'vacancies', enkelvoud: 'Vacature', meervoud: 'Vacatures',
    padPrefix: '/vacature/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'created_at', oplopend: false,
    velden: [
      { kolom: 'title', label: 'Titel', soort: 'tekst', verplicht: true },
      { kolom: 'location', label: 'Plaats', soort: 'tekst' },
      { kolom: 'type', label: 'Dienstverband (Fulltime of Parttime)', soort: 'tekst' },
      { kolom: 'image', label: 'Afbeelding', soort: 'afbeelding' },
      { kolom: 'description', label: 'Tekst', soort: 'html' },
    ],
    blokken: [], alleenLezen: [],
  },
  {
    sleutel: 'oplossingen', tabel: 'solutions', enkelvoud: 'Energieoplossing', meervoud: 'Energieoplossingen',
    padPrefix: '/energieoplossingen/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'sort_order', oplopend: true,
    velden: [...DETAIL_VELDEN, { kolom: 'overview_image', label: 'Afbeelding in het overzicht', soort: 'afbeelding' }],
    blokken: [LIJST, TEKSTVAKKEN, VOORDELEN_OPLOSSING], alleenLezen: [], kerngegevens: true, schuifbeeld: true, dag: true,
  },
  {
    sleutel: 'diensten', tabel: 'services', enkelvoud: 'Dienst', meervoud: 'Diensten',
    padPrefix: '/diensten/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'sort_order', oplopend: true, velden: DETAIL_VELDEN,
    blokken: [LIJST, TEKSTVAKKEN, VOORDELEN], alleenLezen: [], kerngegevens: true,
  },
  {
    sleutel: 'branches', tabel: 'industries', enkelvoud: 'Branche', meervoud: 'Branches',
    padPrefix: '/branches/', titelKolom: 'title', heeftSlug: true, heeftPublicatie: true,
    volgorde: 'sort_order', oplopend: true,
    velden: [{ kolom: 'description', label: 'Korte omschrijving', soort: 'lang' }, ...DETAIL_VELDEN],
    blokken: [LIJST, TEKSTVAKKEN], alleenLezen: [],
  },
  {
    sleutel: 'faq', tabel: 'faq_items', enkelvoud: 'Vraag', meervoud: 'Veelgestelde vragen',
    padPrefix: null, titelKolom: 'question', heeftSlug: false, heeftPublicatie: false,
    volgorde: 'sort_order', oplopend: true,
    velden: [
      { kolom: 'page_slug', label: 'Pagina (bv. home, contact, laadoplossingen)', soort: 'tekst', verplicht: true },
      { kolom: 'question', label: 'Vraag', soort: 'tekst', verplicht: true },
      { kolom: 'answer', label: 'Antwoord', soort: 'lang', verplicht: true },
      { kolom: 'sort_order', label: 'Volgorde', soort: 'getal' },
    ],
    blokken: [], alleenLezen: [],
  },
  ...LIJSTEN,
];

export const soortVoor = (sleutel: string | undefined): ContentSoort | null =>
  SOORTEN.find((s) => s.sleutel === sleutel) ?? null;

export type Rij = Record<string, any>;

/** Kent deze rij een publicatie? De soort heeft een vlag, en de rij heeft de kolom. Bij het team komt de
 *  kolom uit een script (publicatieScript): staat ze er nog niet, dan staat de rij gewoon op de site,
 *  valt er niets te publiceren of te verbergen, en wordt de vlag nooit geschreven. */
export const kentPublicatie = (soort: ContentSoort, rij: Rij | null | undefined): boolean =>
  soort.heeftPublicatie && !!rij && 'published' in rij;

/** Slugs zijn altijd kleine letters (ADR 0024): de site stuurt hoofdletters met een 308 door. */
export function maakSlug(tekst: string): string {
  return (tekst ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

export interface Controle { ok: boolean; fouten: Record<string, string> }

/** Wat er mis is met een concept, per kolom. Leeg = opslaan mag. `vorige` is de rij zoals hij in de
 *  database staat (bij iets nieuws ontbreekt hij): een blok dat daarmee gelijk is, is niet aangeraakt
 *  en wordt niet nagekeken, zodat oude inhoud het opslaan van een ander veld nooit tegenhoudt. */
export function controleer(soort: ContentSoort, rij: Rij, vorige?: Rij | null): Controle {
  const fouten: Record<string, string> = {};
  for (const v of soort.velden) {
    const waarde = rij[v.kolom];
    if (v.verplicht && (waarde == null || String(waarde).trim() === '')) fouten[v.kolom] = `${v.label} is verplicht.`;
    if (v.soort === 'getal' && waarde != null && waarde !== '' && !Number.isFinite(Number(waarde))) fouten[v.kolom] = `${v.label} moet een getal zijn.`;
    if (v.soort === 'afbeelding' && waarde && !String(waarde).startsWith('/media/')) {
      fouten[v.kolom] = 'Een afbeelding komt uit de eigen media en begint met /media/.';
    }
    const gewijzigd = !vorige || String(vorige[v.kolom] ?? '') !== String(waarde ?? '');
    if (v.keur && gewijzigd && !fouten[v.kolom] && waarde != null && String(waarde) !== '') {
      const fout = v.keur(String(waarde));
      if (fout) fouten[v.kolom] = `${v.label}: ${fout}`;
    }
  }
  if (soort.heeftSlug) {
    const slug = String(rij.slug ?? '');
    if (!slug) fouten.slug = 'De slug is verplicht.';
    else if (slug !== maakSlug(slug)) fouten.slug = 'Een slug bestaat uit kleine letters, cijfers en streepjes.';
  }
  for (const blok of soort.blokken) {
    if (!(blok.kolom in rij)) continue;
    const oud = vorige ? vorige[blok.kolom] : undefined;
    if (zelfdeBlok(oud, rij[blok.kolom])) continue;
    if (!leesBlok(blok.soort, oud).ok) {
      fouten[blok.kolom] = `${blok.label}: dit blok heeft een vorm die de bewerker niet kent. Het is hier niet te wijzigen.`;
      continue;
    }
    const fout = controleerBlok(blok, rij[blok.kolom]);
    if (fout) fouten[blok.kolom] = fout;
  }
  // Alleen als de rij in de database de kolom heeft: zonder het script valt er niets na te kijken.
  if (soort.kerngegevens && heeftKerngegevens(vorige) && KERNGEGEVENS in rij) {
    const fout = controleerKerngegevens(vorige![KERNGEGEVENS], rij[KERNGEGEVENS]);
    if (fout) fouten[KERNGEGEVENS] = fout;
  }
  if (soort.plaats && heeftPlaats(vorige)) Object.assign(fouten, controleerPlaats(vorige!, rij));
  if (soort.schuifbeeld && heeftSchuifbeeld(vorige) && SCHUIFBEELD in rij) {
    const fout = controleerSchuifbeeld(vorige![SCHUIFBEELD], rij[SCHUIFBEELD]);
    if (fout) fouten[SCHUIFBEELD] = fout;
  }
  if (soort.dag && heeftDag(vorige) && DAG in rij) {
    const fout = controleerDag(vorige![DAG], rij[DAG]);
    if (fout) fouten[DAG] = fout;
  }
  return { ok: Object.keys(fouten).length === 0, fouten };
}

/** Alleen de kolommen die deze soort mag schrijven; systeemvelden blijven buiten schot. Een blok gaat
 *  alleen mee als het anders is dan in `vorige` (de rij in de database; bij iets nieuws een leeg blok),
 *  de bewerker de vorm in de database herkent en de nieuwe inhoud klopt. In elk ander geval blijft de
 *  kolom buiten de schrijfactie en dus ongewijzigd. "In het kort", de plaats, het schuifbeeld en de dag
 *  gaan alleen mee als de rij in de database de kolom heeft en er iets is gewijzigd; bij iets nieuws
 *  (geen `vorige`) dus nooit. Bij de dag kan wat er meegaat ook geen waarde zijn: leegmaken.
 *  De publicatie gaat bij een bestaande rij alleen mee als ze is gewijzigd: wie een ander veld bewaart,
 *  schrijft de vlag niet. */
export function schrijfbaar(soort: ContentSoort, rij: Rij, vorige?: Rij | null): Rij {
  const uit: Rij = {};
  for (const v of soort.velden) {
    if (!(v.kolom in rij)) continue;
    const waarde = rij[v.kolom];
    if (v.soort === 'getal') uit[v.kolom] = waarde === '' || waarde == null ? 0 : Number(waarde);
    else uit[v.kolom] = waarde === '' ? null : waarde;
  }
  for (const blok of soort.blokken) {
    if (!(blok.kolom in rij)) continue;
    const oud = vorige ? vorige[blok.kolom] : undefined;
    if (zelfdeBlok(oud, rij[blok.kolom])) continue;
    if (!leesBlok(blok.soort, oud).ok || controleerBlok(blok, rij[blok.kolom])) continue;
    uit[blok.kolom] = rij[blok.kolom];
  }
  if (soort.kerngegevens && heeftKerngegevens(vorige) && KERNGEGEVENS in rij) {
    const nieuw = teSchrijven(vorige![KERNGEGEVENS], rij[KERNGEGEVENS]);
    if (nieuw) uit[KERNGEGEVENS] = nieuw;
  }
  if (soort.plaats) Object.assign(uit, schrijfPlaats(vorige, rij));
  if (soort.schuifbeeld && heeftSchuifbeeld(vorige) && SCHUIFBEELD in rij) {
    const nieuw = schuifbeeldTeSchrijven(vorige![SCHUIFBEELD], rij[SCHUIFBEELD]);
    if (nieuw) uit[SCHUIFBEELD] = nieuw.waarde;
  }
  if (soort.dag && heeftDag(vorige) && DAG in rij) {
    const nieuw = dagTeSchrijven(vorige![DAG], rij[DAG]);
    if (nieuw) uit[DAG] = nieuw.waarde;
  }
  if (soort.heeftSlug && 'slug' in rij) uit.slug = rij.slug;
  if (soort.heeftPublicatie && 'published' in rij && (!vorige || (rij.published === true) !== (vorige.published === true))) {
    uit.published = rij.published === true;
  }
  return uit;
}

function ofFout<T>(antwoord: { data: T | null; error: { message: string } | null }, wat: string): T {
  if (antwoord.error) throw new Error(`${wat}: ${antwoord.error.message}`);
  return antwoord.data as T;
}

export async function haalLijst(client: WebsiteClient, soort: ContentSoort): Promise<Rij[]> {
  const antwoord = await client.schema('website').from(soort.tabel).select('*').order(soort.volgorde, { ascending: soort.oplopend });
  return ofFout<Rij[]>(antwoord, `${soort.meervoud} lezen`) ?? [];
}

export async function haalRij(client: WebsiteClient, soort: ContentSoort, id: string): Promise<Rij | null> {
  const antwoord = await client.schema('website').from(soort.tabel).select('*').eq('id', id).maybeSingle();
  return ofFout<Rij | null>(antwoord, `${soort.enkelvoud} lezen`);
}

export interface Opgeslagen {
  rij: Rij;
  redirect: { van: string; naar: string } | null;
  /** De rij is opgeslagen, maar de doorverwijzing niet: het oude adres geeft nu een 404. */
  waarschuwing: string | null;
}

/** Slaat een bestaande rij op. Verandert de slug, dan komt er ná het opslaan een doorverwijzing van het
 *  oude naar het nieuwe pad, en wijzen oudere doorverwijzingen meteen naar het nieuwe pad (geen ketens).
 *
 *  De volgorde is bewust: eerst de rij, dan de doorverwijzing. De site zoekt alleen in de
 *  doorverwijzingen als een pagina niet bestaat (redirectOrNotFound), dus een doorverwijzing naar een
 *  pad dat er nog niet is, is erger dan een pagina zonder doorverwijzing. Er wordt hier nooit een
 *  doorverwijzing verwijderd: staat er al een die vanaf het nieuwe pad vertrekt, dan stopt het opslaan
 *  met een melding, want samen zouden ze een lus vormen zodra de pagina ooit offline gaat. Wie de
 *  pagina toch op dat adres wil, verwijdert die doorverwijzing eerst zelf, op het scherm
 *  Doorverwijzingen (./doorverwijzingen). */
export async function slaOp(client: WebsiteClient, soort: ContentSoort, vorige: Rij, concept: Rij): Promise<Opgeslagen> {
  const controle = controleer(soort, concept, vorige);
  if (!controle.ok) throw new Error(Object.values(controle.fouten)[0]);
  const website = client.schema('website');
  const slugGewijzigd = soort.heeftSlug && !!soort.padPrefix && !!vorige.slug && concept.slug !== vorige.slug;
  const van = slugGewijzigd ? `${soort.padPrefix}${vorige.slug}` : null;
  const naar = slugGewijzigd ? `${soort.padPrefix}${concept.slug}` : null;

  if (slugGewijzigd) {
    const bezet = ofFout<Rij[]>(await website.from(soort.tabel).select('id').eq('slug', concept.slug).neq('id', vorige.id).limit(1), 'Slug controleren') ?? [];
    if (bezet.length) throw new Error(`De slug "${concept.slug}" is al in gebruik bij een andere ${soort.enkelvoud.toLowerCase()}.`);
    const terug = ofFout<Rij[]>(await website.from('redirects').select('from_path, to_path').eq('from_path', naar).limit(1), 'Doorverwijzingen controleren') ?? [];
    if (terug.length) {
      throw new Error(`Er staat al een doorverwijzing van ${naar} naar ${terug[0].to_path}. Verwijder die eerst onder Doorverwijzingen: daarna kan deze slug.`);
    }
  }

  const antwoord = await website.from(soort.tabel).update(schrijfbaar(soort, concept, vorige)).eq('id', vorige.id).select('*').maybeSingle();
  const rij = ofFout<Rij | null>(antwoord, `${soort.enkelvoud} opslaan`);
  // RLS weigert zonder fout: nul rijen terug betekent dat er niets is opgeslagen.
  if (!rij) throw new Error(`${soort.enkelvoud} is niet opgeslagen. Je hebt hier geen schrijfrecht, of de rij bestaat niet meer.`);
  if (!slugGewijzigd) return { rij, redirect: null, waarschuwing: null };

  try {
    ofFout(await website.from('redirects').update({ to_path: naar }).eq('to_path', van).select('from_path'), 'Oude doorverwijzingen bijwerken');
    const gezet = ofFout<Rij[]>(
      await website.from('redirects').upsert({ from_path: van, to_path: naar, status: 301, note: 'slug gewijzigd in de CRM' }, { onConflict: 'from_path' }).select('from_path'),
      'Doorverwijzing aanmaken',
    ) ?? [];
    if (!gezet.length) throw new Error('Doorverwijzing aanmaken: er kwam geen rij terug.');
    return { rij, redirect: { van: van!, naar: naar! }, waarschuwing: null };
  } catch (fout) {
    const reden = fout instanceof Error ? fout.message : String(fout);
    return { rij, redirect: null, waarschuwing: `Opgeslagen, maar de doorverwijzing van ${van} naar ${naar} is niet gelukt (${reden}). Het oude adres geeft nu een 404: zet hem met de hand onder Doorverwijzingen.` };
  }
}

export async function maakNieuw(client: WebsiteClient, soort: ContentSoort, concept: Rij): Promise<Rij> {
  const controle = controleer(soort, concept);
  if (!controle.ok) throw new Error(Object.values(controle.fouten)[0]);
  // Nieuw werk begint als concept: publiceren is een bewuste tweede stap.
  const invoer = { ...schrijfbaar(soort, concept), ...(soort.heeftPublicatie ? { published: false } : {}) };
  const antwoord = await client.schema('website').from(soort.tabel).insert(invoer).select('*').maybeSingle();
  const rij = ofFout<Rij | null>(antwoord, `${soort.enkelvoud} aanmaken`);
  if (!rij) throw new Error(`${soort.enkelvoud} is niet aangemaakt. Je hebt hier geen schrijfrecht.`);
  return rij;
}

/** Het pad van een afbeelding uit de eigen media, klaar om achter het adres van de opslag te zetten.
 *  Een pad in de database kan al gecodeerd zijn (%20 voor een spatie): dan niet nog een keer coderen,
 *  want de opslag kent %2520 niet. */
export function mediaPad(pad: string): string {
  try {
    return encodeURI(decodeURI(pad));
  } catch {
    // Geen geldige codering (een los %-teken): neem het pad zoals het er staat.
    return encodeURI(pad);
  }
}

/** Het volgnummer voor iets nieuws: één hoger dan het hoogste in de lijst, zodat het achteraan komt. */
export function volgendeVolgorde(rijen: Rij[]): number {
  const nummers = rijen.filter((r) => r.sort_order != null).map((r) => Number(r.sort_order)).filter((n) => Number.isFinite(n));
  return nummers.length ? Math.max(...nummers) + 1 : 1;
}

/** Het adres van een rij op de site, of null als het type geen eigen pagina heeft. */
export const padVan = (soort: ContentSoort, rij: Rij): string | null =>
  soort.padPrefix && rij.slug ? `${soort.padPrefix}${rij.slug}` : null;
